/**
 * apps/battle/client/src/App.tsx — Root application component.
 *
 * State machine: Login → ClusterMap → BattleScreen → ClusterMap.
 * No localStorage (React state only, per spec §7 sandbox rule).
 * HUD is always visible when logged in.
 */
import { useState, useEffect } from 'react';
import { QueryClient, QueryClientProvider } from './query.js';
import { api, ApiError } from './api.js';
import type { ClusterState, MapResp, PortalState, ProfileResp, UserRole } from './api.js';
import { Login } from './Login.js';
import { ClusterMap } from './ClusterMap.js';
import { BattleScreen } from './BattleScreen.js';
import { PortalBattleScreen } from './PortalBattleScreen.js';
import { ReviewScreen } from './ReviewScreen.js';
import { Hud } from './Hud.js';

type Screen =
  | { name: 'login' }
  | { name: 'map' }
  | { name: 'battle'; cluster: ClusterState; battleType: string; retryTemplateId?: string }
  | { name: 'portal'; portal: PortalState }
  | { name: 'review'; clusterId: string; clusterName?: string }; // FIX 2e

const qc = new QueryClient();

// Phase 4.2 M9-patch11 — read-only Concept Web mode.
// When /overview?readonly=1 is opened (from the terminal dashboard's [W] key),
// the ClusterMap renders with hover/tooltips intact but all click navigation
// suppressed, and a "Close Concept Web" button replaces the bottom Logout bar.
const IS_READ_ONLY_CONCEPT_WEB: boolean = (() => {
  if (typeof window === 'undefined') return false;
  try {
    return new URLSearchParams(window.location.search).get('readonly') === '1';
  } catch {
    return false;
  }
})();

export function App() {
  const [screen, setScreen] = useState<Screen>({ name: 'login' });
  const [student, setStudent] = useState<{ id: string; email: string; name: string; role: UserRole } | null>(null);
  const [mapData, setMapData] = useState<MapResp | null>(null);
  const [profile, setProfile] = useState<ProfileResp | null>(null);
  const [checkingSession, setCheckingSession] = useState(true);

  // Check existing session on mount.
  useEffect(() => {
    api.get<{ authenticated: boolean; student?: { id: string; email: string; name: string; role: UserRole } }>('/api/session')
      .then((resp) => {
        if (resp.authenticated && resp.student) {
          setStudent(resp.student);
          setScreen({ name: 'map' });
        }
      })
      .catch(() => { /* no session */ })
      .finally(() => setCheckingSession(false));
  }, []);

  // Load map when navigating to map screen.
  useEffect(() => {
    if (screen.name === 'map' && student) {
      loadMap();
      loadProfile();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screen.name, student?.id]);

  async function loadMap() {
    try {
      const data = await api.get<MapResp>('/api/map');
      setMapData(data);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        handleLogout();
      }
    }
  }

  async function loadProfile() {
    try {
      const p = await api.get<ProfileResp>('/api/profile');
      setProfile(p);
    } catch { /* non-fatal */ }
  }

  async function handleLogout() {
    await api.post('/api/logout').catch(() => {});
    setStudent(null);
    setMapData(null);
    setProfile(null);
    setScreen({ name: 'login' });
  }

  function handleLogin(s: { id: string; email: string; name: string; role: UserRole }) {
    // Phase 4.1: SMEs/admins land on /review by default; students stay on map.
    if (s.role === 'sme' || s.role === 'admin') {
      window.location.href = '/review';
      return;
    }
    setStudent(s);
    setScreen({ name: 'map' });
  }

  function handleSelectCluster(cluster: ClusterState) {
    // M9-patch5: Decision order (boss layer is dead — see DEPLOY_phase4-2-m9-patch5.md):
    //  1. All distinct concept questions cleared (conceptCleared>=conceptTotal>0) → open review
    //  2. Otherwise → launch REGULAR within-cluster battle
    const conceptsAllCleared =
      cluster.conceptTotal > 0 && cluster.conceptCleared >= cluster.conceptTotal;
    if (conceptsAllCleared) {
      setScreen({ name: 'review', clusterId: cluster.clusterId, clusterName: cluster.fullName });
      return;
    }

    // 'REGULAR' sentinel — server treats this as the within-cluster path.
    setScreen({ name: 'battle', cluster, battleType: 'REGULAR' });
  }

  function handleSelectPortal(portal: PortalState) {
    if (!portal.available) return; // unavailable portals stay on the content-coming modal
    setScreen({ name: 'portal', portal });
  }

  // FIX 2e: Navigate to review screen from battle completion panel
  function handleReview(clusterId: string) {
    // Find the cluster name from mapData if available
    const clusterName = mapData?.clusters.find((c) => c.clusterId === clusterId)?.fullName;
    setScreen({ name: 'review', clusterId, clusterName });
  }

  // FIX A6: Launch a retry battle pinned to a specific templateId from ReviewScreen.
  // Reuses the 'REGULAR' within-cluster path; server bypass-cleared filter for pinned id.
  function handleRetry(clusterId: string, templateId: string) {
    const cluster = mapData?.clusters.find((c) => c.clusterId === clusterId);
    if (!cluster) return;
    setScreen({ name: 'battle', cluster, battleType: 'REGULAR', retryTemplateId: templateId });
  }

  function handleBattleBack(xpAwarded: number, newLevel: number) {
    // Update profile XP/level optimistically.
    if (profile && xpAwarded > 0) {
      setProfile((prev) => prev ? {
        ...prev,
        gameState: {
          ...prev.gameState,
          totalXp: prev.gameState.totalXp + xpAwarded,
          level: newLevel,
        },
      } : prev);
    }
    setScreen({ name: 'map' });
  }

  if (checkingSession) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100dvh' }}>
        <span style={{ color: 'var(--text-muted)' }}>Loading…</span>
      </div>
    );
  }

  if (screen.name === 'login' || !student) {
    return (
      <QueryClientProvider client={qc}>
        <Login onLogin={handleLogin} />
      </QueryClientProvider>
    );
  }

  return (
    <QueryClientProvider client={qc}>
      <Hud
        name={student.name}
        level={profile?.gameState.level ?? 1}
        totalXp={profile?.gameState.totalXp ?? 0}
        streakDays={profile?.gameState.currentStreakDays ?? 0}
      />

      {screen.name === 'map' && mapData && (
        <ClusterMap
          mapData={mapData}
          onSelectCluster={IS_READ_ONLY_CONCEPT_WEB ? (() => {}) : handleSelectCluster}
          onSelectPortal={IS_READ_ONLY_CONCEPT_WEB ? undefined : handleSelectPortal}
          readOnly={IS_READ_ONLY_CONCEPT_WEB}
        />
      )}

      {screen.name === 'map' && !mapData && (
        <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>
          Loading map…
        </div>
      )}

      {screen.name === 'battle' && (
        <BattleScreen
          clusterId={screen.cluster.clusterId}
          battleType={screen.battleType}
          retryTemplateId={screen.retryTemplateId}
          onBack={handleBattleBack}
          onReview={handleReview}
        />
      )}

      {screen.name === 'portal' && (
        <PortalBattleScreen
          portalKey={screen.portal.portalKey}
          onBack={handleBattleBack}
        />
      )}

      {/* FIX 2e: Review screen */}
      {screen.name === 'review' && (
        <ReviewScreen
          clusterId={screen.clusterId}
          clusterName={screen.clusterName}
          onBack={() => setScreen({ name: 'map' })}
          onRetry={handleRetry}
        />
      )}

      {/* M9-patch11: in read-only Concept Web mode, the bottom Logout bar is hidden
          and a terminal-styled "Close Concept Web" button is rendered top-right. */}
      {IS_READ_ONLY_CONCEPT_WEB ? (
        <button
          type="button"
          className="cw-close-btn"
          onClick={() => {
            // window.opener is set because the parent used window.open(); fall back to history.back().
            try { window.close(); } catch { /* noop */ }
            // If the browser blocked close (no opener), navigate home as a graceful fallback.
            setTimeout(() => {
              if (!window.closed) window.location.href = '/terminal';
            }, 100);
          }}
          aria-label="Close Concept Web"
        >
          [ × CLOSE CONCEPT WEB ]
        </button>
      ) : (
        <div style={{ padding: '0.5rem 1rem', borderTop: '1px solid var(--border)', marginTop: 'auto', display: 'flex', justifyContent: 'flex-end' }}>
          <button
            className="btn btn-secondary text-sm"
            onClick={handleLogout}
            style={{ padding: '0.375rem 0.75rem', fontSize: '0.8rem' }}
          >
            Logout
          </button>
        </div>
      )}
    </QueryClientProvider>
  );
}
